/**
 * fuelLevel
 * ─────────
 * Pure helpers for the loan-car fuel-level quick-pick used in both
 * LoanCarAllocationPanel (set at allocation time) and VehicleReceivedModal
 * (recorded at handover time).
 *
 * The convention is a single canonical line at the top of the notes
 * textarea, like `Fuel: Full`. Helpers stay UI-free so they can be unit
 * tested in isolation and shared without dragging React in.
 */

// Display order matters — the three pills render in this order.
export const FUEL_OPTIONS = [
    { key: 'full',           label: 'Full' },
    { key: 'more-than-half', label: 'More than half' },
    { key: 'less-than-half', label: 'Less than half' },
];

// Matches a leading `Fuel:` line (case-insensitive) plus its trailing
// newline if present, so removal/replacement doesn't leave a blank line
// behind. The label portion is captured for parse but ignored for apply.
// `m` flag so the anchor matches the start of any line — handy when the
// user has manually moved the line elsewhere in the notes.
const FUEL_LINE_RE = /^[ \t]*Fuel:[ \t]*([^\r\n]*)\r?\n?/im;

/**
 * Parse the active fuel-level key from a notes string. Returns one of the
 * FUEL_OPTIONS keys, or null if no recognised fuel line is present.
 */
export const parseFuelFromNotes = (notes) => {
    if (!notes) return null;
    const m = notes.match(FUEL_LINE_RE);
    if (!m) return null;
    const value = (m[1] || '').trim().toLowerCase();
    const hit = FUEL_OPTIONS.find(o => o.label.toLowerCase() === value);
    return hit ? hit.key : null;
};

/**
 * Return a new notes string with the fuel line set to `optionKey`, or
 * removed entirely when `optionKey` is null. The fuel line is canonical-
 * ised to the first line — even if the user manually moved it elsewhere
 * — so subsequent parse/apply round-trips stay stable.
 */
export const applyFuelToNotes = (notes, optionKey) => {
    const existing = notes ?? '';
    const stripped = existing.replace(FUEL_LINE_RE, '');
    if (!optionKey) return stripped;
    const opt = FUEL_OPTIONS.find(o => o.key === optionKey);
    if (!opt) return existing;
    const line = `Fuel: ${opt.label}`;
    return stripped.length > 0 ? `${line}\n${stripped}` : line;
};